// src/lib/livret-autotranslate.js — L6 (ÉTAPE 2)
//
// Auto-traduction AU SAVE : traduit FR → EN/ES UNIQUEMENT les langues cibles
// VIDES. Une cible NON vide (saisie/correction manuelle OU traduction antérieure
// conservée) n'est JAMAIS touchée → garantit « jamais d'écrasement ».
// Non bloquant : tout échec / fallback Claude laisse la cible vide (le save
// continue). Réutilise l'endpoint livret_translate (Claude Haiku 4.5).
//
// Mutualise le helper `tr()` jadis dupliqué dans LivretConfigEditor +
// LivretModuleEditor (qui exposaient un bouton « Traduire » manuel, retiré L6).

import { supabase } from "../supabase";

async function getAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

// Un appel de traduction. Renvoie la chaîne traduite, ou null si indisponible
// (HTTP KO, fallback Claude, ou champ vide) → le caller laisse la cible vide.
async function translateOne(sourceText, targetLocale, context, headers) {
  const resp = await fetch("/api/beds24-mapping?action=livret_translate", {
    method: "POST",
    headers,
    body: JSON.stringify({
      action: "livret_translate",
      source_text: sourceText,
      target_locale: targetLocale,
      context,
    }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || !json.ok || json.fallback || !json.translated) return null;
  return json.translated;
}

/**
 * Remplit les langues cibles VIDES depuis le FR ; laisse les cibles non vides
 * intactes. Jamais d'écrasement, jamais bloquant.
 *
 * @param {object} p
 * @param {string} p.fr      texte source FR
 * @param {string} p.en      valeur EN actuelle (vide ⇒ candidate à traduction)
 * @param {string} p.es      valeur ES actuelle (vide ⇒ candidate à traduction)
 * @param {string} p.context "title" | "content"
 * @returns {Promise<{en: string, es: string}>} valeurs finales (jamais null)
 */
export async function autoTranslateEmptyLangs({ fr, en, es, context }) {
  const frTrim = (fr || "").trim();
  const enTrim = (en || "").trim();
  const esTrim = (es || "").trim();

  const needEn = !!frTrim && !enTrim;
  const needEs = !!frTrim && !esTrim;
  // Rien à traduire (pas de source, ou les 2 cibles déjà remplies).
  if (!needEn && !needEs) return { en: enTrim, es: esTrim };

  let headers;
  try {
    headers = await getAuthHeaders();
  } catch (_) {
    return { en: enTrim, es: esTrim }; // pas d'auth → ne bloque pas le save
  }

  const [enRes, esRes] = await Promise.all([
    needEn
      ? translateOne(frTrim, "en", context, headers).catch(() => null)
      : Promise.resolve(null),
    needEs
      ? translateOne(frTrim, "es", context, headers).catch(() => null)
      : Promise.resolve(null),
  ]);

  return {
    en: needEn ? enRes || "" : enTrim,
    es: needEs ? esRes || "" : esTrim,
  };
}

/**
 * M7 — Re-traduction avec ÉCRASEMENT (mode overwrite du rattrapage).
 * Export SÉPARÉ : autoTranslateEmptyLangs ci-dessus reste FIGÉ (mode fill).
 *
 * Traduit FR → EN + ES même si les cibles sont déjà remplies (écrasement des
 * corrections manuelles — confirmé par une modale explicite côté UI).
 *
 * GARDE ANTI-WIPE (condition dure pessimist B1) : le mécanisme fatal à éviter
 * est buildLangPatch qui OMET les locales falsy alors que le PATCH serveur
 * REMPLACE la colonne jsonb entière → une locale absente du patch quand les
 * autres y figurent = wipe silencieux de cette locale. Ce helper rend le wipe
 * IMPOSSIBLE PAR CONSTRUCTION : sur TOUT échec de traduction d'une locale
 * (translateOne null OU exception), il retourne la valeur EXISTANTE de cette
 * locale (jamais "" ni undefined si elle existait) → la locale reste présente
 * dans le patch ET langChanged reste false pour elle (0 écriture inutile).
 *
 * IDEMPOTENCE NON GARANTIE (pessimist B2, best-effort assumé) : le cache de
 * livret_translate est PAR-LAMBDA (cold start = cache froid) — un re-run peut
 * renvoyer une traduction quasi identique mais non byte-identique et donc
 * re-patcher. Quand le cache est chaud et la traduction identique à
 * l'existant, langChanged=false ⇒ 0 PATCH (idempotence naturelle, jamais
 * promise). La réponse serveur expose `cached` mais ne suffit pas à le
 * garantir → on documente « best-effort » sans le promettre.
 *
 * @param {object} p
 * @param {string} p.fr       texte source FR (vide ⇒ existant intact, 0 appel)
 * @param {string} p.en       valeur EN existante (préservée si échec)
 * @param {string} p.es       valeur ES existante (préservée si échec)
 * @param {string} p.context  "title" | "content"
 * @param {function} [p.getAuthHeaders] injection (tests) — défaut : session Supabase
 * @returns {Promise<{en: string, es: string, failed: Array<"en"|"es">}>}
 */
export async function retranslateAllLangs({
  fr,
  en,
  es,
  context,
  getAuthHeaders: getHeaders,
}) {
  const frTrim = (fr || "").trim();
  const enTrim = (en || "").trim();
  const esTrim = (es || "").trim();

  // FR vide ou absent → cibles EXISTANTES intactes, 0 appel réseau
  // (miroir de la garde fr-vide du mode fill : jamais d'écrasement sans source).
  if (!frTrim) return { en: enTrim, es: esTrim, failed: [] };

  let headers;
  try {
    headers = await (getHeaders || getAuthHeaders)();
  } catch (_) {
    // Pas d'auth → AUCUNE locale traduite : existant intact, 2 échecs remontés.
    return { en: enTrim, es: esTrim, failed: ["en", "es"] };
  }

  const [enRes, esRes] = await Promise.all([
    translateOne(frTrim, "en", context, headers).catch(() => null),
    translateOne(frTrim, "es", context, headers).catch(() => null),
  ]);

  const failed = [];
  if (enRes === null) failed.push("en");
  if (esRes === null) failed.push("es");

  return {
    // Échec (null) ⇒ valeur EXISTANTE retournée — JAMAIS "" ni undefined à la
    // place d'une valeur existante (anti-wipe B1).
    en: enRes !== null ? enRes : enTrim,
    es: esRes !== null ? esRes : esTrim,
    failed,
  };
}
